'use client';
import Avo from '@susu/custom-made/Avo';
import { ThemeContext } from '@susu/custom-made/providers';
import { actions as optionActions } from '@susu/custom-made/providers/reducers/option';
import { applyBusinessRulesForStylePresetSelection } from '@susu/custom-made/utils/businessRules';
import { Clients } from '@susu/custom-made/utils/constants';
import {
  createLayerUrlFromTemplate,
  createPreviewData,
} from '@susu/custom-made/utils/imageUrlProvider/imageUrlProvider';
import type {
  BusinessRules,
  HiddenOptionsAndValues,
  LayerDefinitions,
  Option,
  StylePreset,
  typeOptionValue,
} from '@susu/custom-made/utils/interfaces';
import { getConfigurationTypeBySection } from '@susu/custom-made/utils/navigation';
import { getAvoEventParamsWithOption } from '@susu/custom-made/utils/utils';
import cn from 'classnames';
import Image from 'next/image';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import Button from '../button/button';
import Preview from '../preview/preview';
import styles from './baseStyleCarousel.module.scss';
import { LayerDefinition } from '@susu/custom-made/utils/preview/layers';
import { getMatchingConfigurationTypeIdForGivenExtraConfigurationTypeId } from '@susu/custom-made/utils/productDefinition';

interface PresetWithLayers {
  name: string;
  layers: LayerDefinition[];
  fallbackImage: LayerDefinition;
}

export default function Carousel({
  stylePresets,
  section,
  optionValues,
  layerDefinitions,
  businessRules,
  options,
}: Readonly<{
  stylePresets: StylePreset[];
  section: string;
  optionValues: typeOptionValue;
  layerDefinitions: LayerDefinitions;
  businessRules: BusinessRules;
  options: Option[];
}>) {
  const { state, dispatch } = useContext(ThemeContext);
  const [selectedStyleIndex, setSelectedStyleIndex] = useState(0);
  const [presetsWithLayers, setPresetsWithLayers] = useState<PresetWithLayers[]>([]);
  const configurationTypeId = useMemo(() => getConfigurationTypeBySection(section), [section]);

  const applySelection = useCallback(() => {
    if (businessRules && options && optionValues && state.product && state.section && stylePresets) {
      //apply preset rules
      const tmpSelectedOptions = structuredClone(state.selectedOptions);
      let tmpHiddenOptionsAndValues: HiddenOptionsAndValues = structuredClone(state.hiddenOptionsAndValues);

      applyBusinessRulesForStylePresetSelection(
        stylePresets[selectedStyleIndex],
        tmpSelectedOptions,
        state.selectedFabric!.id,
        businessRules,
        tmpHiddenOptionsAndValues,
        optionValues,
        configurationTypeId,
        options,
        state.product,
        true,
        state.growthbookFeatures['show-logs'],
      );

      dispatch({
        type: optionActions.SET_HIDDEN_OPTIONS_AND_VALUES,
        payload: tmpHiddenOptionsAndValues,
      });
      dispatch({
        type: optionActions.SELECT_OPTION,
        payload: tmpSelectedOptions,
      });
    }
  }, [
    businessRules,
    options,
    optionValues,
    state.product,
    state.section,
    stylePresets,
    state.selectedFabric,
    selectedStyleIndex,
  ]);

  const confirmSelection = (style: StylePreset) => {
    dispatch({
      type: optionActions.SELECT_BASE_STYLE,
      payload: {
        configurationType: configurationTypeId,
        baseStyle: style,
      },
    });

    dispatch({
      type: optionActions.CHANGE_BASE_STYLE_STATUS,
      payload: false,
    });

    state.client === Clients.onLine && Avo.styleOptionConfirm(getAvoEventParamsWithOption(state, style.name));
  };

  const changeStyle = (selectedStyleIndex: number) => {
    setSelectedStyleIndex(selectedStyleIndex);
    state.client === Clients.onLine &&
      Avo.styleOptionSelectCm(getAvoEventParamsWithOption(state, stylePresets[selectedStyleIndex].name));
  };

  useEffect(() => {
    if (stylePresets && section) {
      state.selectedBaseStyle[configurationTypeId] &&
        stylePresets.forEach((stylePreset, index) => {
          if (state.selectedBaseStyle[configurationTypeId].id === stylePreset.id) {
            setSelectedStyleIndex(index);
          }
        });
    }
  }, [state.selectedBaseStyle, section, stylePresets]);

  useEffect(() => {
    if (layerDefinitions && optionValues && state.selectedFabric && configurationTypeId) {
      const selectedFabric = state.selectedFabric;
      Promise.all(
        stylePresets.map(async preset => {
          //it can happen that stylePreset is not selecting all possible option values, so we need to check the state as well in order to create full preview
          let presetOptionsRecord: Record<number, number> = structuredClone(state.selectedOptions[configurationTypeId]);
          preset.presetOptions.forEach(presetOption => {
            if (optionValues[presetOption.optionValueId]) {
              presetOptionsRecord[presetOption.optionId] = presetOption.optionValueId;
            }
          });

          // To handle extra configurations which have different configuration type id but using definitions from base product
          const baseConfigurationTypeId =
            getMatchingConfigurationTypeIdForGivenExtraConfigurationTypeId(+configurationTypeId);
          const definition = layerDefinitions.configurationTypes[baseConfigurationTypeId];

          const { fallbackUrl, layerDefinitions: imageDefinitions } = definition;

          const processedImageTemplates = imageDefinitions.map(def => {
            return createLayerUrlFromTemplate(
              def.imageTemplate,
              optionValues,
              { [configurationTypeId]: presetOptionsRecord },
              selectedFabric,
              configurationTypeId,
            );
          });

          const previewData = await createPreviewData(processedImageTemplates, fallbackUrl);

          return {
            name: preset.name,
            ...previewData,
          };
        }),
      ).then(presetsWithLayers => {
        setPresetsWithLayers(presetsWithLayers);
      });
    }
  }, [layerDefinitions, optionValues, stylePresets, state.selectedFabric, state.selectedOptions, section]);

  useEffect(() => {
    applySelection();
  }, [selectedStyleIndex, applySelection]);

  return (
    <div
      className={cn(styles.container, {
        [styles.inStore]: state.client === Clients.inStore,
      })}
    >
      <div className={styles.header} data-test-id="base-style-header">
        <Image src="https://cdn.suitsupply.com/image/upload/svg/base_style.svg" alt="" height={24} width={24} />
        Choose your style
      </div>
      <div className={styles.sliderContainer} data-test-id="base-style-carousel">
        <div
          className={cn(styles.slideArrow, styles.slidePrev, {
            [styles.disabled]: selectedStyleIndex === 0,
          })}
          onClick={() => {
            if (selectedStyleIndex !== 0) {
              changeStyle(selectedStyleIndex - 1);
            }
          }}
        >
          Previous
        </div>
        <div
          className={cn(styles.slideArrow, styles.slideNext, {
            [styles.disabled]: presetsWithLayers.length === selectedStyleIndex + 1,
          })}
          onClick={() => {
            if (presetsWithLayers.length > selectedStyleIndex + 1) {
              changeStyle(selectedStyleIndex + 1);
            }
          }}
        >
          Next
        </div>

        {presetsWithLayers.map((style, index) => (
          <div
            className={cn(styles.slideImageContainer, {
              [styles.show]: index === selectedStyleIndex,
            })}
            key={style.name}
            data-test-id={`base-style-${style.name}-image`}
          >
            <Preview layers={style.layers} fallbackImage={style.fallbackImage} />
          </div>
        ))}

        <div className={styles.slideButtonContainer} data-test-id="slide-buttons">
          {stylePresets.map((style, index) => (
            <div key={style.name} data-test-id={`base-style-${style.id}-id`} data-index={index}>
              <div
                className={cn(styles.slideButtonContent, {
                  [styles.selected]: index === selectedStyleIndex,
                })}
                data-test-id={`base-style-${style.name}-name`}
                onClick={() => {
                  changeStyle(index);
                }}
              >
                {style.name}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className={styles.styleDescription} data-test-id="base-style-description">
        {stylePresets[selectedStyleIndex].description}
      </div>
      <div className={styles.buttonContainer}>
        <Button
          isFullSize={true}
          onClick={() => {
            confirmSelection(stylePresets[selectedStyleIndex]);
          }}
          testId="base-style-confirm-btn"
        >
          Confirm
        </Button>
      </div>
    </div>
  );
}
